import { ReactNode } from 'react';
import { FlexContainer } from '../../containers/ContentContainers/FlexContainer';
import { FormBuilderInputType, FormItem } from './formBuilder.types';
import { Typography } from '@mui/material';

const gapPx = 20;

export interface FormBuilderLayoutShellProps {
  children: ReactNode;
  flexBasis?: string;
  minWidth?: number;
  nowrap?: boolean;
}

const formBuilderRowResponsiveSx = {
  '@media screen and (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
} as const;

const formBuilderItemResponsiveSx = {
  '@media screen and (max-width: 768px)': {
    width: '100%',
    margin: '10px 0',
  },
} as const;

/**
 * Horizontal row wrapper for grouped FormBuilder fields.
 * @param props - Layout shell props.
 * @returns Form builder group container.
 */
export const FormBuilderGroup = ({
  children,
  flexBasis,
  minWidth,
  nowrap,
}: FormBuilderLayoutShellProps) => (
  <FlexContainer
    direction="row"
    alignItems="flex-start"
    justifyContent="flex-start"
    gap={2.5}
    flexGrow={1}
    flexWrap={!nowrap}
    flexBasis={flexBasis}
    minWidth={minWidth != null ? `${minWidth}px` : undefined}
    width="100%"
    height="auto"
    overflowAllVisible
    sx={formBuilderRowResponsiveSx}
  >
    {children}
  </FlexContainer>
);

/**
 * Single FormBuilder field shell with vertical spacing defaults.
 * @param props - Layout shell props.
 * @returns Form builder item container.
 */
export const FormBuilderItem = ({
  children,
  flexBasis,
  minWidth,
}: FormBuilderLayoutShellProps) => (
  <FlexContainer
    direction="column"
    flexGrow={1}
    margin="8px 0"
    flexBasis={flexBasis}
    minWidth={minWidth != null ? `${minWidth}px` : undefined}
    width="100%"
    height="auto"
    overflowAllVisible
    sx={formBuilderItemResponsiveSx}
  >
    {children}
  </FlexContainer>
);

/**
 * Section label row for FormBuilder configs.
 * @param props - Label text children.
 * @returns Label row.
 */
export const FormBuilderLabel = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <FormBuilderGroup>
      <Typography
        sx={{
          fontFamily: `'Montserrat', 'Lato', 'sans-serif'`,
          marginBottom: '-11px',
        }}
        variant="titleMedium"
      >
        {children}
      </Typography>
    </FormBuilderGroup>
  );
};

const defaultMinWidth = 130;
const minWidth: Record<FormBuilderInputType, number> = {
  text: defaultMinWidth,
  number: defaultMinWidth,
  phone: defaultMinWidth,
  checkbox: defaultMinWidth,
  select: defaultMinWidth,
  autocomplete: defaultMinWidth,
  radioGroup: defaultMinWidth,
  date: defaultMinWidth,
  multiSelect: 0,
  checkboxGroup: 0,
  richText: defaultMinWidth,
  noteField: defaultMinWidth,
  searchBar: defaultMinWidth,
};

const getItemMinWidthTotal = <T,>(formItem: FormItem<T>): number => {
  if (Array.isArray(formItem)) {
    formItem = { type: 'group', items: formItem };
  }
  if (formItem.type === 'group') {
    return (
      formItem.items.reduce((acc, item) => {
        return acc + getItemMinWidthTotal(item);
      }, 0) +
      formItem.items.length * gapPx
    );
  } else if (formItem.type === 'input') {
    return formItem.formLayoutMinWidth ?? minWidth[formItem.input];
  } else if (formItem.type === 'passthrough') {
    return formItem.minWidth || 0;
  }
  return 0;
};

/**
 * Computes minimum width for a form item given the form width.
 * @param formItem - FormBuilder item definition.
 * @param formWidth - Available form width in pixels.
 * @returns Minimum width in pixels.
 */
export const getFormBuilderItemMinWidth = <T,>(
  formItem: FormItem<T>,
  formWidth: number
): number => {
  const computedMinWidth = getItemMinWidthTotal(formItem);
  return computedMinWidth > formWidth ? formWidth : computedMinWidth;
};

/**
 * Computes flex-basis for a form item within a sibling group.
 * @param formItem - FormBuilder item definition.
 * @param siblingCount - Number of siblings in the row.
 * @returns CSS flex-basis value.
 */
export const getFlexBasis = <T,>(
  formItem: FormItem<T>,
  siblingCount: number
): string => {
  if (Array.isArray(formItem)) {
    formItem = { type: 'group', items: formItem };
  }
  const gapCount = Math.max(siblingCount - 1, 0);
  return `calc(${formItem.size || 100 / siblingCount}% - ${
    gapCount * (gapPx / 2)
  }px)`;
};
